// How to show a CLAIM without dressing it up as a fact.
//
// The feed reports what Telegram channels said, filtered through an LLM
// extractor. Everything needed to say how solid any given row is was already
// being stored and thrown away: the extractor's own confidence, how the position
// was fixed, how many independent channels carried it, and — since migration 029
// — who is blamed and whether that was STATED or INFERRED.
//
// `basis` is the load-bearing field. Publishing a model's inference as though
// the post stated it is the failure the attribution feature exists to avoid, so
// an inferred attribution says the word "inferred" IN THE VISIBLE TEXT. Styling
// alone is not enough: a colour or an italic is deniable, and a screenshot of
// this feed is the thing that ends up in someone else's report.
//
// `attrs.target` is the physical thing hit; `attrs.targetFaction` is whose it
// was. They are different fields and must never be merged into one label.

import { reliabilityOf } from './channelmeta';
import { channelLabel } from './livekinds';

export interface ClaimPart {
  text: string;
  title: string;
  /** Renders muted/marked — an estimate, an inference, or a lone source. */
  weak?: boolean;
  /** A caution about whether the claim holds at all — a denial, a contested
   *  outcome, sources all rated low. The one kind of part a compact feed row
   *  still shows while the rest of the line waits in its details. */
  caution?: boolean;
}

const esc = (s: string) =>
  s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
const escAttr = (s: string) =>
  s.replace(/[&<>"]/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** Distinct channel handles: `@` stripped, case folded, so one channel posting
 *  as "@A", "a" and "A" counts once. */
function distinctHandles(names: unknown[]): string[] {
  const out = new Set<string>();
  for (const n of names) {
    const c = str(n).replace(/^@/, '').toLowerCase();
    if (c) out.add(c);
  }
  return [...out];
}

function channelHandles(attrs: Record<string, unknown>): string[] {
  const sources = attrs.sources;
  if (Array.isArray(sources) && sources.length) {
    const names = distinctHandles(
      (sources as Record<string, unknown>[]).map((s) => s?.channel));
    if (names.length) return names;
  }
  const solo = str(attrs.channel);
  return solo ? [solo] : [];
}

function channelCount(attrs: Record<string, unknown>): number {
  return channelHandles(attrs).length;
}

/** The operator's standing rating of the channels behind this row, when it is
 *  worth saying. Words, not styling, for the same reason inferred attribution
 *  carries the word "inferred": a screenshot must keep the caveat. */
function reliabilityPart(attrs: Record<string, unknown>): ClaimPart | null {
  const handles = channelHandles(attrs);
  if (!handles.length) return null;
  const ratings = handles.map(reliabilityOf);
  if (ratings.every((r) => r === 'low')) {
    return {
      text: handles.length === 1 ? 'low-reliability source'
        : 'low-reliability sources',
      weak: true,
      caution: true,
      title: 'Every channel carrying this is rated LOW reliability in the '
        + 'channel list. Corroboration from them corroborates less.',
    };
  }
  if (handles.length === 1 && ratings[0] === 'high') {
    return {
      text: 'trusted source',
      title: 'A single channel, but one rated HIGH reliability in the channel '
        + 'list. Still uncorroborated.',
    };
  }
  return null;
}

/** How the position was fixed, when that is worth saying. A clean gazetteer hit
 *  on a specific place says nothing — silence is the signal that it is fine. */
function positionPart(attrs: Record<string, unknown>): ClaimPart | null {
  const basis = str(attrs.positionBasis);
  const km = typeof attrs.match_km === 'number' ? attrs.match_km : null;
  const matchBasis = str(attrs.match_basis);
  if (matchBasis && km !== null) {
    // The event was not located at a mapped place; it was associated with the
    // most likely nearby site by KIND. That inference deserves its distance.
    const what = matchBasis === 'launch-origin'
      ? 'nearest missile base' : 'nearest naval base';
    return {
      text: `${what}, ${km} km`,
      title: `No mapped place at the reported point. Associated with the ${what}, `
        + `${km} km away, because of the event kind — this is an inference, not `
        + `a stated location.`,
      weak: true,
    };
  }
  if (basis === 'area-fallback') {
    return { text: 'region only', weak: true,
      title: 'The specific place could not be found; this is the containing '
        + 'region, so the pin is not the location.' };
  }
  if (basis === 'area-estimate') {
    return { text: 'estimated within region', weak: true,
      title: 'The report names a part of a region ("northern Israel"), not a '
        + 'place; the pin is an estimate inside that part, not the location.' };
  }
  if (basis === 'offshore-estimate' || basis === 'model-estimate') {
    return { text: 'estimated position', weak: true,
      title: 'No gazetteer match. The coordinate is the model\'s estimate.' };
  }
  if (attrs.vague === true || str(attrs.precision) === 'area') {
    return { text: 'approx. area', weak: true,
      title: 'The report names an area, not a point — the pin is its centre.' };
  }
  return null;
}

/** An actor claim. `stated` and `inferred` MUST read differently. */
function actorPart(name: string, basis: string, confidence: unknown,
                   verb: string): ClaimPart | null {
  if (!name) return null;
  const conf = typeof confidence === 'number'
    ? ` The extractor's confidence in this is ${Math.round(confidence * 100)}%.` : '';
  // Anything that is not explicitly 'stated' is treated as inferred — the same
  // direction the backend's clean_event defaults in, and for the same reason:
  // the weaker reading is the safe one for an accusation.
  if (basis === 'stated') {
    return { text: `${verb} ${name}`,
      title: `The source post states this.${conf}` };
  }
  return {
    text: `${verb} ${name} (inferred)`,
    weak: true,
    title: `INFERRED by the extractor — the post does not say this.${conf} `
      + 'Treat it as a lead, not as a reported fact.',
  };
}

export interface ClaimOpts {
  /** Skip the channel count. Set on a situation card head, which already shows
   *  the corroboration for the whole session — a per-event count beside it would
   *  be a second, smaller, contradictory number. */
  skipCorroboration?: boolean;
  /** Skip the "low confidence" caveat. Set wherever `confidenceChip` renders
   *  beside this line: the chip carries the number AND the word, so the part
   *  here would be the same caveat twice on one row. */
  skipConfidence?: boolean;
  /** A compact feed row shows ONLY the cautions and its details skip them,
   *  so the two halves of one row never say the same caveat twice. */
  cautions?: 'only' | 'skip';
}

export function claimParts(attrs: Record<string, unknown>,
                           opts: ClaimOpts = {}): ClaimPart[] {
  const parts: ClaimPart[] = [];

  // A dispute leads everything: a later post denying the event happened is
  // the strongest caveat a row can carry, and it must be words in the visible
  // text (the same reasoning as "inferred") — the event stays, the denial is
  // another claim, and the reader sees both with names attached.
  const d = attrs.disputed as Record<string, unknown> | undefined;
  if (d && typeof d === 'object') {
    const by = channelLabel(String(d.by ?? '').replace(/^@/, ''));
    const note = String(d.note ?? '').trim();
    parts.push({
      caution: true,
      text: by ? `⚠ disputed by ${by}` : '⚠ disputed',
      title: (note ? `"${note}" — ` : '')
        + 'A later post denies this event happened. The report stays: one '
        + 'source claims it, another denies it.',
    });
  }

  // A CONTESTED outcome is a different claim from a denial and must read as
  // one: the event happened, but a later post says it went differently than
  // reported ("the Patriots failed", "the base was hit despite the
  // interception claim"). Same rule as disputed and as `basis` — the caveat
  // is words in the visible text, because a screenshot of this feed ends up
  // in someone else's report.
  const c = attrs.contested as Record<string, unknown> | undefined;
  if (c && typeof c === 'object') {
    const by = channelLabel(String(c.by ?? '').replace(/^@/, ''));
    const note = String(c.note ?? '').trim();
    parts.push({
      caution: true,
      text: by ? `⚠ outcome contested by ${by}` : '⚠ outcome contested',
      title: (note ? `"${note}" — ` : '')
        + 'A later post agrees this happened but contradicts what was '
        + 'reported about how it went. Both claims stand.',
    });
  }

  // Corroboration next: the strongest positive signal the feed has, and the
  // one thing a single row could never show.
  const channels = opts.skipCorroboration ? 0 : channelCount(attrs);
  if (channels === 1) {
    parts.push({ text: '1 channel', weak: true,
      title: 'Only one channel has reported this. Uncorroborated.' });
  } else if (channels > 1) {
    parts.push({ text: `${channels} channels`,
      title: `${channels} independent channels carried this.` });
  }

  // Reliability rides regardless of skipCorroboration: the card head hides the
  // per-event channel COUNT because the card shows its own, but "everything
  // carrying this is low-reliability" is about trust, not arithmetic, and no
  // aggregate number contradicts it.
  const reliability = reliabilityPart(attrs);
  if (reliability) parts.push(reliability);

  const position = positionPart(attrs);
  if (position) parts.push(position);

  // The physical target, which is NOT the same question as whose it was.
  const target = str(attrs.target);
  if (target) {
    parts.push({ text: `target: ${target}`,
      title: 'The thing reported hit, as described in the post.' });
  }

  const blamed = actorPart(str(attrs.attributedTo), str(attrs.attributedToBasis),
                           attrs.attributedToConfidence, 'blames');
  if (blamed) parts.push(blamed);

  const struck = actorPart(str(attrs.targetFaction), str(attrs.targetFactionBasis),
                           attrs.targetFactionConfidence, 'against');
  if (struck) parts.push(struck);

  // Only ever surfaced when it is LOW. A "90% confident" badge on every row is
  // noise; the extractor is told to use <= 0.3 for a post it could not place in
  // time, and that is worth seeing.
  // Same rule as the chip, deliberately: a caller showing both must not get
  // "low confidence" here beside a chip that says corroboration answered it.
  const confidence = typeof attrs.confidence === 'number' ? attrs.confidence : null;
  if (!opts.skipConfidence && confidence !== null
      && chipBand(attrs, confidencePct(confidence) / 100).band === 'low') {
    parts.push({ text: 'low confidence', weak: true,
      title: `The extractor rated this ${confidencePct(confidence)}% and `
        + 'nothing has corroborated it since — it may not be a real, current, '
        + 'locatable event.' });
  }

  if (opts.cautions === 'only') return parts.filter((p) => p.caution);
  if (opts.cautions === 'skip') return parts.filter((p) => !p.caution);
  return parts;
}

/** The extractor's own rating, banded ONCE.
 *
 *  Thresholds are the extractor's own contract, not a taste call: it is told
 *  to rate a post it cannot place in time at <= 0.3, `clean_event` defaults an
 *  UNRATED event to 0.5, and 0.6 is what the prompt's own worked example calls
 *  moderate. So <= 0.4 was rated down deliberately, and > 0.7 was rated up
 *  deliberately; the band between them is where "the model said nothing much"
 *  lives. 40% is the same threshold the claim line has always used — it does
 *  not move, because both readings have to agree on what "low" means. */
export const LOW_MAX_PCT = 40;
export const HIGH_MIN_PCT = 70;

export type ConfidenceBand = 'low' | 'mid' | 'high';

/** The percentage the chip PRINTS — and the only thing anything bands on.
 *
 *  Banding the raw float instead is what made 70% sometimes green and
 *  sometimes amber: 0.700 and 0.704 both print "70%" while straddling
 *  HIGH_MIN, and 0.400 and 0.404 both print "40%" while straddling LOW_MAX.
 *  Two rows showing the same number in different colours is the feed asking
 *  the reader to trust a distinction it refuses to show them.
 *
 *  So the invariant is: **the colour must be recomputable from what is on
 *  screen.** Round first, band second, and the thresholds are integers because
 *  that is now the unit of comparison (and because `0.07 * 100` is
 *  7.000000000000001 — float thresholds have no business here at all). */
export const confidencePct = (c: number): number =>
  Math.round(Math.min(1, Math.max(0, c)) * 100);

export function confidenceBand(c: number): ConfidenceBand {
  const pct = confidencePct(c);
  if (pct <= LOW_MAX_PCT) return 'low';
  if (pct <= HIGH_MIN_PCT) return 'mid';
  return 'high';
}

/** Three distinct channels. Two is a forward and a paste as often as it is two
 *  witnesses, and this feed's own dedupe folds verbatim reposts precisely
 *  because channels copy each other wholesale. */
export const CORROBORATION_MIN = 3;

/** The band the CHIP shows, which is deliberately NOT just the extractor's.
 *
 *  `attrs.confidence` rates ONE POST. The extractor is asked "is this a real,
 *  current, locatable event?" about the clearest post in a fold, and it cannot
 *  see that four other channels are carrying the same thing — an in-batch fold
 *  (`alsoPostIds`) yields one event with ONE rating however many posts went
 *  into it, and `merge_into` across batches only ever takes the MAX of the
 *  per-post numbers. Corroboration never touched it.
 *
 *  So a row could say "5 channels" and "low" at once, which is the feed
 *  contradicting itself: five channels reporting explosions at Erbil is not a
 *  low-confidence claim, whatever the model made of the first post it read.
 *  Corroboration is not a different question from the one `confidence` asks —
 *  it is the strongest available evidence FOR that question, arriving after
 *  the model had its say.
 *
 *  So corroboration raises the band, and the rules on when it may NOT are the
 *  interesting half:
 *
 *  - it takes CORROBORATION_MIN distinct channels;
 *  - it never raises when the operator has rated EVERY channel behind the row
 *    low reliability. That is `reliabilityPart`'s standing rule and it is also
 *    the reader's own definition of a weak claim: a couple of poor sources
 *    carrying something nobody else has;
 *  - a DENIAL cancels it outright. A later post saying the event did not happen
 *    is the strongest caveat a row carries and nothing may talk over it;
 *  - it raises ONE band, never two. Corroboration answers "did this happen",
 *    which lifts a row off the floor; it does not make the extractor's reading
 *    of the post good.
 *
 *  The NUMBER never moves. It is the record of what the model actually said,
 *  and the tooltip names both readings whenever they disagree — the same
 *  contract `basis` keeps for an inferred attribution.
 *
 *  Neither does the COLOUR (2026-09-01, the third round on this chip). The
 *  band returned here decides the caveat WORD and the tooltip; the chip paints
 *  the number's own band regardless. It used to repaint a lifted row and mark
 *  the lift with a caret, and the operator read that as "60 is orange here
 *  and green there" without ever seeing the caret — which settles it: a colour
 *  the reader cannot recompute from the number beside it is inconsistent
 *  however well it is footnoted.
 *
 *  `channels` lets a situation head lift on the SITUATION's channel list —
 *  the count its own meta line prints — instead of the headline event's, so a
 *  head cannot say "4 channels" and "low" at once. */
export function chipBand(attrs: Record<string, unknown>, c: number,
                         channels?: string[]): { band: ConfidenceBand; lifted: boolean } {
  const base = confidenceBand(c);
  if (base === 'high') return { band: base, lifted: false };
  const handles = channels ? distinctHandles(channels) : channelHandles(attrs);
  const denied = Boolean(attrs.disputed && typeof attrs.disputed === 'object');
  const allLow = handles.length > 0
    && handles.every((h) => reliabilityOf(h) === 'low');
  if (handles.length < CORROBORATION_MIN || allLow || denied) {
    return { band: base, lifted: false };
  }
  return { band: base === 'low' ? 'mid' : 'high', lifted: true };
}

/** The rating as a chip: number, bar, and — at the bottom band only — a word.
 *
 *  This used to be opt-in, shown only in member lists, on the argument that a
 *  "90%" badge on every row of a feed being SCANNED is noise. Reversed
 *  2026-09-01 by product decision: the number was the one piece of the claim
 *  the reader could not get without opening something, and a row's other
 *  strength signals (channels, position, disputed) all read at a glance while
 *  the extractor's own doubt only did so below 0.4. The noise argument is
 *  answered by SIZE rather than by silence — the chip is 10px, the bar is
 *  20px, and only the caution band takes colour in the text.
 *
 *  Which part carries what is the basis rule, unchanged. The WORD "low" is the
 *  caveat and it is in the visible text, because a screenshot of this feed ends
 *  up in someone else's report. The bar and the colour are reinforcement. The
 *  top band gets no word at all: "this one is fine" is exactly what silence
 *  already says everywhere else in this line (positionPart stays quiet on a
 *  clean gazetteer hit for the same reason).
 *
 *  The tooltip says outright that this is the MODEL'S certainty and not
 *  corroboration, because a green bar beside a single unreliable channel is
 *  the one way this chip could be read as verification.
 *
 *  Unrated renders NOTHING, never "0%" or "—". Confidence is absent on rows
 *  the extractor never scored, and an absence must not read as a low score. */
export function confidenceChip(attrs: Record<string, unknown>,
                               prefix = '',
                               opts: { what?: string; note?: string;
                                       channels?: string[] } = {}): string {
  const raw = typeof attrs.confidence === 'number' ? attrs.confidence : null;
  if (raw === null || !Number.isFinite(raw)) return '';
  const pct = confidencePct(raw);
  // The COLOUR is the number's own band, and nothing else moves it.
  const colour = confidenceBand(pct / 100);
  // The VERDICT — whether the caveat word is said — is the lifted band.
  const { band, lifted } = chipBand(attrs, pct / 100, opts.channels);
  const n = (opts.channels ? distinctHandles(opts.channels)
                           : channelHandles(attrs)).length;
  const what = opts.what ?? 'a real, current, locatable event';
  const title = `The extractor rated this report ${pct}% — its own confidence `
    + `that the post describes ${what}, judged from ONE post.`
    + (lifted
      // The disagreement said out loud. A reader looking at an amber 38% has
      // to be able to find out why it is not red, and "5 channels" three
      // inches away in the claim line is not an explanation of THIS chip.
      ? ` ${n} independent channels are carrying it — evidence the extractor`
        + ' did not have when it rated the post, so the claim is stronger than'
        + ' the rating alone says'
        + (colour === 'low' ? ' and it is not called low' : '')
        + '. The number and its colour are still what the model said.'
      : band === 'low'
      // Worded for both lanes: the events prompt says <= 0.3 for a post it
      // could not place in time, the news prompt has no such rule, and a
      // tooltip that named the events rule on a news row would be a lie.
      ? ' At or below 40% it is flagging real doubt — that is the band it uses'
        + ' when it cannot judge a post confidently, and nothing has'
        + ' corroborated it since.'
      : '')
    + " This is the model's own certainty, not corroboration — a high rating"
    + ' on one uncorroborated channel is still one uncorroborated channel.'
    + (opts.note ? ` ${opts.note}` : '');
  // The lift is NOT painted. It used to recolour the chip and mark itself
  // with a caret; the operator read that as "60 is orange here and green
  // there" and did not see the caret, which settles it — a colour the reader
  // cannot recompute from the number beside it is inconsistent however well
  // it is footnoted. So the bar and the text take the NUMBER's band, the lift
  // withdraws the caveat word and explains itself in the tooltip, and the
  // channel count in the claim line beside it is the visible reason. A reader
  // who misses that under-reads the claim, which is the safe way to be wrong.
  return `${prefix}<b class="conf conf-${colour}" title="${escAttr(title)}"><i `
    + `class="conf-bar" aria-hidden="true"><span style="width:${pct}%"></span></i>`
    + `${pct}%${band === 'low' ? ' low' : ''}</b>`;
}


/** The claim-strength line for a feed row. Empty string when there is nothing
 *  worth saying, so a clean well-corroborated event stays quiet. */
export function claimHtml(attrs: Record<string, unknown>,
                          opts: ClaimOpts = {}): string {
  const parts = claimParts(attrs, opts);
  if (!parts.length) return '';
  return `<span class="claim">${parts.map((p) =>
    `<span class="claim-bit${p.weak ? ' weak' : ''}${p.caution ? ' caution' : ''}" title="${escAttr(p.title)}">${
      esc(p.text)}</span>`).join('<i>·</i>')}</span>`;
}
